import { type FC, type HTMLAttributes, type ReactNode, memo } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { cn } from '@/lib/utils.ts'

import { BlockerIcon } from '@/components/BlockerIcon.tsx'
import { BlockerStoryIdMarker } from '@/components/BlockerStoryIdMarker.tsx'
import { CopyButton } from '@/components/CopyButton.tsx'
import { withStoryDropzone } from '@/components/enhancers/withStoryDropZone.tsx'
import { EditableBlockers, TypedEditableBlockers } from '@/components/form/Blockers.tsx'
import { EditableComments, TypedEditableComments } from '@/components/form/Comments.tsx'
import { EditableDeadline, TypedEditableDeadline } from '@/components/form/Deadline.tsx'
import { Description, EditableDescription, TypedEditableDescription } from '@/components/form/Description.tsx'
import { EditableEstimate, TypedEditableEstimate } from '@/components/form/Estimate.tsx'
import { Owners, TypedEditableOwners } from '@/components/form/Owners.tsx'
import { RequestedBy, TypedEditableRequestedBy } from '@/components/form/RequestedBy.tsx'
import { StoryDeleteButton } from '@/components/form/StoryDeleteButton.tsx'
import { StoryHistoryButton } from '@/components/form/StoryHistoryButton.tsx'
import { EditableStoryLabels, TypedEditableStoryLabels } from '@/components/form/StoryLabels.tsx'
import { EditableStoryType, TypedEditableStoryType } from '@/components/form/StoryType.tsx'
import { EditableTasks, TypedEditableTasks } from '@/components/form/Tasks.tsx'
import { EditableTitle, TypedEditableTitle } from '@/components/form/Title.tsx'
import { EditableAcceptedAt, TypedEditableAcceptedAt } from '@/components/form/status/AcceptedAt.tsx'
import { EditableStatus, TypedEditableStatus } from '@/components/form/status/Status.tsx'
import { StatusButton } from '@/components/form/status/StatusButton.tsx'
import { NEW_STORY_ID } from '@/constants/DEFAULT_VALUES.ts'
import { STORY_STATUSES } from '@/constants/STORY_STATUSES.ts'
import { AttachmentProvider } from '@/contexts/AttachmentContext.tsx'
import { useCreateStory } from '@/requests/mutations/useCreateStory.ts'
import { useQueryStory } from '@/requests/queries/useQueryStory.ts'
import { chainFunctions } from '@/utils/call.ts'
import { clickCheckboxWithId } from '@/utils/dom.ts'
import { isTemporaryId } from '@/utils/key.ts'
import { GlobalPubSub } from '@/utils/pubsub.ts'
import { scrollToStory } from '@/utils/scroll.ts'
import { submitResidualData } from '@/utils/story.ts'

type Props = {
  storyId: string
  children?: ReactNode
}

export const StoryForm = memo(({ storyId, children }: Props) => {
  const { story } = useQueryStory(storyId)
  const { create, removeCache } = useCreateStory(storyId)

  if (!story?.id) return null

  return (
    <form id={`story_${story.id}`} className="flex flex-col gap-3 p-2 bg-surface-neutralLight">
      {children}
      <div className="flex gap-2 px-2 bg-surface-componentBackground border border-border-default-neutral rounded-xs">
        <div className="flex flex-col items-start">
          <TypedEditableStoryType
            modes={['edit']}
            storyId={story.id}
            value={story.storyType}
            status={story.status}
            editView={EditableStoryType}
          />
        </div>
        <div className="flex items-center w-full">
          <TypedEditableTitle
            modes={['edit']}
            storyId={story.id}
            value={story.title}
            editView={EditableTitle}
            callback={() => document.getElementById(`save-story-${storyId}`)?.click()}
          />
        </div>
      </div>

      {storyId === NEW_STORY_ID ? (
        <div className="flex items-center justify-between gap-4">
          <Button
            type="button"
            variant="outline"
            className="py-1 px-[10px] h-8 font-EN-KR-body2-regular rounded-xs"
            onClick={() => removeCache()}
          >
            Close
          </Button>
          <Button
            id={`save-story-${storyId}`}
            type="button"
            variant="secondary"
            className="py-1 px-[10px] h-8 font-EN-KR-body2-bold rounded-xs"
            disabled={!story?.title}
            onClick={() => {
              submitResidualData(storyId)
              create().then(() => GlobalPubSub.publish(GlobalPubSub.Signals.TOGGLE_STORY_FORM, storyId, false))
            }}
          >
            Save
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-4">
          <Button
            id={`update-story-${storyId}`}
            type="button"
            variant="secondary"
            className="py-1 px-[10px] h-8 font-EN-KR-body2-bold rounded-xs"
            disabled={!story?.title}
            onClick={() => {
              submitResidualData(storyId)
              GlobalPubSub.publish(GlobalPubSub.Signals.TOGGLE_STORY_FORM, storyId, false)
            }}
          >
            Collapse & Update
          </Button>
        </div>
      )}

      <div className="flex flex-col items-start gap-3">
        <Separator className="w-full h-0 border border-dashed border-tb-grey-300" />
      </div>

      <div className="flex items-start justify-between">
        <div className="flex w-[108px] items-center">
          <CopyButton
            label="ID"
            value={`#${story.id}`}
            disabled={isTemporaryId(story.id)}
            defaultImgSrc="/images/story-id-copy-icon.svg"
            copiedImgSrc="/images/copied-icon.svg"
          />
        </div>

        <div className="flex items-center gap-0.5">
          <StoryHistoryButton story={story} />
          <Button
            type="button"
            size="icon"
            variant="icon"
            disabled={story.id === NEW_STORY_ID}
            onClick={() =>
              create(true)
                .then((response) => {
                  const newStory = response?.data?.executeCommand?.data?.[0] as Story | undefined
                  if (!newStory) return
                  chainFunctions(
                    () => GlobalPubSub.publish(GlobalPubSub.Signals.SELECT_LABEL, null),
                    () => clickCheckboxWithId('toggle_blockers_filter', 'unchecked'),
                    () => {
                      clickCheckboxWithId('unscheduled_checkbox', 'checked')
                    },
                    () => GlobalPubSub.publish(GlobalPubSub.Signals.TOGGLE_STORY_FORM, newStory?.id, true),
                    () => scrollToStory(newStory.id, 'smooth'),
                  )
                })
                .catch(console.error)
            }
          >
            <img src="/images/story-clone-icon.svg" alt="clone this story" />
          </Button>
          <StoryDeleteButton story={story} />
        </div>
      </div>

      <div className="grid grid-rows-2 grid-cols-2 gap-y-[1px] bg-contents-onDarkPrimary rounded-xs overflow-hidden h-[80px]">
        <GridCell>
          <TypedEditableRequestedBy modes={['view']} storyId={story.id} value={story.requestedBy} view={RequestedBy} />
        </GridCell>
        <GridCell>
          <TypedEditableOwners storyId={story.id} view={Owners} value={story.owners} modes={['view']} />
        </GridCell>
        <GridCell>
          <TypedEditableDeadline
            modes={['edit']}
            storyId={story.id}
            storyType={story.storyType}
            editView={EditableDeadline}
            value={story.deadline}
          />
          <TypedEditableEstimate
            modes={['edit']}
            storyId={story.id}
            value={story.estimate}
            storyType={story.storyType}
            status={story.status}
            editView={EditableEstimate}
          />
        </GridCell>
        <GridCell>
          <Label htmlFor={`story-status-${storyId}`} variant="title">
            State
          </Label>
          <div className="flex gap-1 items-center">
            {story.status === STORY_STATUSES.ACCEPTED ? (
              <TypedEditableAcceptedAt
                modes={['edit']}
                storyId={story.id}
                value={story.acceptedAt}
                editView={EditableAcceptedAt}
                status={story.status}
              />
            ) : (
              <StatusButton
                storyId={story.id}
                status={story.status}
                estimate={story.estimate}
                storyType={story.storyType}
              />
            )}
            <TypedEditableStatus
              modes={['edit']}
              storyId={story.id}
              value={story.status}
              editView={EditableStatus}
              estimate={story.estimate}
              storyType={story.storyType}
            />
          </div>
        </GridCell>
      </div>

      <div>
        <TypedEditableBlockers
          modes={['edit']}
          storyId={story.id}
          value={story.blockers}
          editView={EditableBlockers}
          placeholder="Add a blocker or impediment"
        />
        {(story.blockedStoryIds ?? []).length > 0 && (
          <div className="py-1 px-2 rounded-xs flex gap-2 items-start mt-1">
            <BlockerIcon size="sm" isBlocked={false} />
            <div className="flex flex-col gap-1">
              <div className="font-EN-KR-body2-regular text-contents-primary">
                Blocks {story.blockedStoryIds.length} Stories
              </div>
              <div className="flex gap-1">
                {story.blockedStoryIds.map((id: string) => (
                  <BlockerStoryIdMarker key={id} storyId={id} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div>
        <TypedEditableDescription
          modes={['view', 'edit']}
          storyId={story.id}
          value={story.description}
          view={Description}
          editView={EditableDescription}
          placeholder="Add a description"
        />
      </div>

      <div>
        <TypedEditableStoryLabels
          modes={['edit']}
          storyId={story.id}
          value={story.labels}
          editView={EditableStoryLabels}
        />
      </div>

      <div>
        <TypedEditableTasks
          modes={['edit']}
          storyId={story.id}
          value={story.tasks}
          editView={EditableTasks}
          placeholder="Add a task"
        />
      </div>

      <div>
        <TypedEditableComments
          modes={['edit']}
          storyId={story.id}
          value={story.comments}
          editView={EditableComments}
          placeholder="Add a comment or upload an attachment. Maximum file size is 20MB."
        />
      </div>
    </form>
  )
})

const DroppableStoryForm = withStoryDropzone(StoryForm)

export const StoryFormWithAttachment = (props: Props) => (
  <AttachmentProvider>
    <DroppableStoryForm {...props} />
  </AttachmentProvider>
)

interface GridCellProps extends HTMLAttributes<HTMLDivElement> {}
const GridCell: FC<GridCellProps> = ({ children, className }) => {
  return (
    <div className={cn('px-2 flex justify-between items-center bg-surface-neutralMedium', className)}>{children}</div>
  )
}
